import { withPoolReferences } from '../../utils/pool-content'
import { useState } from 'react'
import { message } from 'antd'
import { campaignV2Api } from '../../services'
import {
   ICatalogItem,
   IContentPool,
   IContentReference,
   IContentRevision,
   IPhoneDefinition,
   IStrategyDefinition,
   TContentDefinition,
   TContentKind,
} from '../../interfaces/campaign-v2.types'
import { IContentSummary, useCampaignV2Store } from '../../store/editor.store'
import { addRef, removeRef, replaceRef } from '../../utils/config'

/** The five places a pool can hold references. */
export type TSlot =
   | 'outboundScripts'
   | 'inboundScripts'
   | 'strategies'
   | 'conversationContent'
   | 'personas'
   | 'humanScripts'

export const SLOT_KIND: Record<TSlot, TContentKind> = {
   outboundScripts: 'phone',
   inboundScripts: 'phone',
   strategies: 'strategy',
   conversationContent: 'conversation',
   personas: 'persona',
   humanScripts: 'phone',
}

export const summaryOf = (
   rev: IContentRevision,
   shared: boolean
): IContentSummary => {
   const d = rev.definition as TContentDefinition
   const strat = d as IStrategyDefinition
   const phone = d as IPhoneDefinition
   return {
      id: String(rev.contentId),
      kind: rev.kind,
      name: d.name,
      shared,
      businessTypeId: d.businessTypeId,
      counts:
         rev.kind === 'strategy'
            ? {
                 sms: strat.sms?.length || 0,
                 email: strat.email?.length || 0,
                 voicemail: strat.voicemail?.length || 0,
              }
            : undefined,
      callDirections: rev.kind === 'phone' ? phone.callDirections : undefined,
      leadTypes: rev.kind === 'phone' ? phone.leadTypes : undefined,
   }
}

export const summaryOfItem = (item: ICatalogItem): IContentSummary => ({
   id: String(item.contentId),
   kind: item.kind,
   name: item.name,
   shared: item.shared,
   businessTypeId: item.businessTypeId,
   counts: item.counts,
   callDirections: item.callDirections,
   leadTypes: item.leadTypes,
})

/**
 * A structural change needs a new identity (customize); a wording-only
 * change on owned content is a revise + publish on the same identity.
 * Handoff sections 12, 13 and 21.
 */
export const isStructuralChange = (
   kind: TContentKind,
   before: TContentDefinition,
   after: TContentDefinition
) => {
   if (kind === 'strategy') {
      const a = before as IStrategyDefinition
      const b = after as IStrategyDefinition
      const same = (x: Array<{ id?: number }>, y: Array<{ id?: number }>) =>
         x.length === y.length &&
         x.every((it, i) => it.id !== undefined && it.id === y[i]?.id)
      return (
         !same(a.sms || [], b.sms || []) ||
         !same(a.email || [], b.email || []) ||
         !same(a.voicemail || [], b.voicemail || [])
      )
   }
   if (kind === 'phone') {
      const a = before as IPhoneDefinition
      const b = after as IPhoneDefinition
      return (
         a.sections.length !== b.sections.length ||
         a.sections.some(
            (s, i) => String(s.sectionId) !== String(b.sections[i]?.sectionId)
         ) ||
         a.callType !== b.callType ||
         JSON.stringify(a.leadTypes) !== JSON.stringify(b.leadTypes) ||
         a.businessTypeId !== b.businessTypeId
      )
   }
   return before.businessTypeId !== after.businessTypeId
}

/** Only the wording fields the revise API accepts, keyed by item id. */
export const wordingChanges = (
   kind: TContentKind,
   before: TContentDefinition,
   after: TContentDefinition
): Record<string, unknown> => {
   const out: Record<string, unknown> = {}
   if (after.name !== before.name) out.name = after.name
   if (kind === 'strategy') {
      const a = before as IStrategyDefinition
      const b = after as IStrategyDefinition
      const sms = b.sms
         .filter((it, i) => it.template !== a.sms[i]?.template)
         .map((it) => ({ id: it.id, template: it.template }))
      const voicemail = b.voicemail
         .filter((it, i) => it.template !== a.voicemail[i]?.template)
         .map((it) => ({ id: it.id, template: it.template }))
      const email = b.email
         .filter(
            (it, i) =>
               it.subject !== a.email[i]?.subject ||
               it.body !== a.email[i]?.body
         )
         .map((it) => ({ id: it.id, subject: it.subject, body: it.body }))
      if (sms.length) out.sms = sms
      if (voicemail.length) out.voicemail = voicemail
      if (email.length) out.email = email
   }
   if (kind === 'phone') {
      const a = before as IPhoneDefinition
      const b = after as IPhoneDefinition
      if (b.firstMessage !== a.firstMessage) out.firstMessage = b.firstMessage
      const sections = b.sections
         .filter((s, i) => s.content !== a.sections[i]?.content)
         .map((s) => ({ sectionId: s.sectionId, content: s.content }))
      if (sections.length) out.sections = sections
   }
   if (kind === 'conversation') {
      const a = before as { content: string }
      const b = after as { content: string }
      if (a.content !== b.content) out.content = b.content
   }
   if (kind === 'persona') {
      const a = before as { guidance: unknown }
      const b = after as { guidance: unknown }
      if (JSON.stringify(a.guidance) !== JSON.stringify(b.guidance))
         out.guidance = b.guidance
   }
   return out
}

/** Strip ids and order so create/customize arrays are clean (section 21). */
export const forCreate = (
   kind: TContentKind,
   def: TContentDefinition
): TContentDefinition => {
   if (kind === 'strategy') {
      const s = def as IStrategyDefinition
      return {
         ...s,
         sms: s.sms.map(({ template }) => ({ template })),
         email: s.email.map(({ subject, body, contentType }) => ({
            subject,
            body,
            contentType: contentType || 'text',
         })),
         voicemail: s.voicemail.map(({ template }) => ({ template })),
      }
   }
   if (kind === 'phone') {
      const p = def as IPhoneDefinition
      return {
         ...p,
         sections: p.sections.map((s, i) => ({
            name: s.name,
            type: s.type,
            order: s.order ?? i + 1,
            content: s.content,
            ...(s.templateType ? { templateType: s.templateType } : {}),
         })),
      }
   }
   return def
}

/**
 * Bind, replace and remove references on the active pool, and turn a
 * finished editor into the right backend calls.
 */
export const useContentActions = (poolId: string) => {
   const { setConfig, indexContent } = useCampaignV2Store()
   const [busy, setBusy] = useState(false)

   const updatePool = (fn: (pool: IContentPool) => IContentPool) =>
      setConfig((c) => ({
         ...c,
         contentPools: c.contentPools.map((p) => (p.id === poolId ? fn(p) : p)),
      }))

   const bind = (slot: TSlot, ref: IContentReference) =>
      updatePool((p) => withPoolReferences(p, slot, addRef(p[slot] || [], ref)))
   const rebind = (slot: TSlot, oldId: string, ref: IContentReference) =>
      updatePool((p) =>
         withPoolReferences(p, slot, replaceRef(p[slot] || [], oldId, ref), {
            from: oldId,
            to: ref.id,
         })
      )
   const unbind = (slot: TSlot, id: string) =>
      updatePool((p) =>
         withPoolReferences(p, slot, removeRef(p[slot] || [], id))
      )

   /** "Use as is": an exact reference to an existing library item. */
   const useExisting = async (slot: TSlot, item: ICatalogItem) => {
      setBusy(true)
      try {
         const rev = item.revisionId
            ? await campaignV2Api().readRevision(
                 item.revisionId,
                 String(item.contentId),
                 true
              )
            : await campaignV2Api().acquireRevision(item.kind, item.contentId)
         const ref = { id: String(rev.contentId), revisionId: rev.id }
         indexContent([summaryOfItem(item)])
         bind(slot, ref)
         return ref
      } catch (e) {
         message.error((e as Error).message || 'Could not use that item')
         return null
      } finally {
         setBusy(false)
      }
   }

   /** A brand-new owned item written in the editor. */
   const createNew = async (
      slot: TSlot,
      kind: TContentKind,
      def: TContentDefinition
   ) => {
      setBusy(true)
      try {
         const rev = await campaignV2Api().createContent(
            kind,
            forCreate(kind, def)
         )
         const ref = { id: String(rev.contentId), revisionId: rev.id }
         indexContent([summaryOf(rev, false)])
         bind(slot, ref)
         return ref
      } catch (e) {
         message.error((e as Error).message || 'Could not save that')
         return null
      } finally {
         setBusy(false)
      }
   }

   /**
    * Save edits to a bound item. Shared → customize into a new owned
    * identity and rebind. Owned + structural → same. Owned + wording only →
    * revise then publish; the pool keeps the identity, the reference moves
    * to the new revision.
    */
   const saveEdits = async (
      slot: TSlot,
      current: IContentReference,
      shared: boolean,
      base: IContentRevision,
      after: TContentDefinition
   ) => {
      setBusy(true)
      try {
         const api = campaignV2Api()
         const kind = base.kind
         if (shared || isStructuralChange(kind, base.definition, after)) {
            const rev = await api.customizeContent(
               { id: current.id, revisionId: base.id },
               {
                  ...forCreate(kind, after),
                  ...(base.legacyBusinessTypeId &&
                  after.businessTypeId === base.definition.businessTypeId
                     ? { businessTypeId: base.legacyBusinessTypeId }
                     : {}),
               } as Partial<TContentDefinition>
            )
            const ref = { id: String(rev.contentId), revisionId: rev.id }
            indexContent([summaryOf(rev, false)])
            rebind(slot, current.id, ref)
            message.success(
               shared
                  ? 'Saved as your own copy for this campaign'
                  : 'Saved as a new version of this item'
            )
            return ref
         }
         const changes = wordingChanges(kind, base.definition, after)
         if (!Object.keys(changes).length) return current
         const draft = await api.reviseContent(
            { id: current.id, revisionId: base.id },
            changes
         )
         const published = await api.publishContent(
            { id: current.id, revisionId: draft.id },
            base.id
         )
         const ref = { id: current.id, revisionId: published.id }
         indexContent([summaryOf(published, false)])
         rebind(slot, current.id, ref)
         message.success(
            'Wording published. Every campaign using this item gets it on the next unprepared touch.'
         )
         return ref
      } catch (e) {
         message.error((e as Error).message || 'Could not save those changes')
         return null
      } finally {
         setBusy(false)
      }
   }

   return { busy, bind, rebind, unbind, useExisting, createNew, saveEdits }
}
